/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   workflowDashboardHelp.ts
 *     Description     :   Help content data for the Workflow Dashboard page
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-16
 *     Modified        :   2026-02-16 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.12.0 (Enterprise Dependency Architecture)
 *     Feature         :   135-ui-data-alignment
 * =========================================================================================
 *
 *  Structured help content consumed by <PageHelpPanel> on the Workflow
 *  Dashboard. Covers page purpose, key concepts, how-to steps, and
 *  related links.
 *
 *  @module modules/workflows/help
 *  @spec 135-ui-data-alignment
 *  @story US6 - Self-Service Help
 */

import type { PageHelpContent } from '../../../components/ui/PageHelpPanel';

// =============================================================================
// TOOLTIP STRINGS
// =============================================================================

/** Tooltip text for each metric card on the Workflow Dashboard */
export const workflowMetricTooltips = {
  totalResources:
    'Total number of discovered lakehouses and warehouses across all workspaces. Includes both complete and incomplete resources.',
  fullyOnboarded:
    'Resources where every onboarding step is complete: discovered, workspace assigned, storage mapped, backup configured, and first backup verified.',
  avgCompletion:
    'Average onboarding completion percentage across all resources. 100% means every resource is fully onboarded.',
  bottleneck:
    'The onboarding step where the most resources are currently stuck. Focus here to unblock the largest group.',
  driftDetected:
    'Drift means the actual resource state no longer matches the expected configuration. For example, a storage mapping was removed or a backup schedule was disabled after onboarding.',
} as const;

/** Tooltip text for each tab */
export const workflowTabTooltips = {
  lakehouse:
    'Track onboarding progress for Fabric Lakehouses — discovery, storage mapping, backup configuration, and first-run verification.',
  warehouse:
    'Track onboarding progress for Fabric Warehouses — discovery, storage mapping, and metadata configuration.',
  collection:
    'Monitor the health of automated data collection jobs that keep the control plane in sync with Fabric.',
} as const;

/** Tooltip text for filter options */
export const workflowFilterTooltips = {
  all: 'Show all resources regardless of onboarding status.',
  complete: 'Show only resources where every onboarding step is finished.',
  incomplete: 'Show resources that still have pending onboarding steps.',
  stuck: 'Resources where the next step requires manual action or an error is blocking progress.',
} as const;

/** Tooltip text for sort controls */
export const workflowSortTooltips = {
  sortDropdown: 'Choose how to order the resource list.',
  sortDirection: 'Toggle between ascending (lowest first) and descending (highest first) order.',
} as const;

/** Tooltip for the health banner in Collection & Sync tab */
export const collectionHealthTooltip =
  'Healthy: last run completed within 24 hours. Warning: 24–48 hours since last run. Critical: more than 48 hours or last run failed.';

// =============================================================================
// HELP PANEL CONTENT
// =============================================================================

export const workflowDashboardHelp: PageHelpContent = {
  pageTitle: 'Workflow Dashboard',
  purpose:
    'Track onboarding progress for every resource, identify bottlenecks, and navigate to the next action for incomplete resources.',
  sections: [
    {
      title: 'Key Concepts',
      concepts: [
        {
          term: 'Onboarding Steps',
          definition:
            'Each resource moves through a fixed set of steps (Discovered → Storage Assigned → Backup Configured → First Backup). Steps are auto-detected from existing data — no manual tracking.',
        },
        {
          term: 'Completion %',
          definition:
            'The percentage of onboarding steps completed for a single resource (0–100%). Fully onboarded = 100%.',
        },
        {
          term: 'Bottleneck Step',
          definition:
            'The step where the most resources are currently stuck. Resolving this step unblocks the largest group of resources at once.',
        },
        {
          term: 'Stuck',
          definition:
            'A resource is "stuck" when the next onboarding step requires manual action (e.g., creating a storage mapping) or an error is preventing progress.',
        },
        {
          term: 'Drift',
          definition:
            'Mismatch between expected and actual resource state. For example, a backup was disabled after onboarding completed.',
        },
      ],
    },
    {
      title: 'How To Use This Page',
      steps: [
        { step: 1, instruction: 'Select a workflow tab (Lakehouse, Warehouse, or Collection & Sync) to view that category.' },
        { step: 2, instruction: 'Review the summary metrics at the top to understand overall health.' },
        { step: 3, instruction: 'Use the filter dropdown to focus on incomplete or stuck resources.' },
        { step: 4, instruction: 'Click a resource card to view its detail page.' },
        { step: 5, instruction: 'Follow the "Next step" link on each card to jump directly to the action needed.' },
      ],
    },
    {
      title: 'Tab Descriptions',
      paragraphs: [
        'Lakehouse Onboarding — Tracks each lakehouse through discovery, storage mapping, backup config, and first-run verification.',
        'Warehouse Onboarding — Tracks each warehouse through discovery, storage mapping, and metadata configuration.',
        'Collection & Sync — Monitors automated data collection job health, including last run status, frequency, drift detection, and error rates.',
      ],
      defaultCollapsed: true,
    },
    {
      title: 'Related Pages',
      links: [
        { label: 'Lakehouses List', href: '/lakehouses' },
        { label: 'Warehouses List', href: '/warehouses' },
        { label: 'Data Collection Settings', href: '/settings/data-collection' },
        { label: 'Dashboard', href: '/dashboard' },
      ],
      defaultCollapsed: true,
    },
  ],
};
